import type {TenantConfiguration, UserSetting} from "~~/api/models";
import {useNuxtApp} from "#app";
import {useItemStore} from "~/store/item";
import {piniaPluginPersistedstate} from "#imports";
import {normalizeTenantId} from '~/utils/tenantId';
import {z} from "zod";

type ConfigurationValue = string | number | boolean | null;

const ORGANISATION_SCOPE_KEY = 'organisation';

type OrganisationScopedUserSetting = UserSetting & {
  organisationScoped?: boolean;
};

export const useConfigurationStore = defineStore('configuration', () => {
  /*
      Types
   */
  interface StoredTenantConfigurations {
    [tenantId: string]: Record<string, ConfigurationValue>;
  }
  interface StoredTenantUserSettings {
    [tenantId: string]: Record<string, UserSetting>;
  }

  /*
      State
   */
  const storedConfigurations = ref<StoredTenantConfigurations>({});
  const storedUserSettings = ref<StoredTenantUserSettings>({});
  let userSettingsGeneration = 0;

  /*
      Composable
   */
  const nuxtApp = useNuxtApp();
  const itemStore = useItemStore();

  /*
      Actions
   */
  const getConfiguredTenantId = (tenantId: string | null = itemStore.activeTenant as string | null): string => {
    return normalizeTenantId(String(tenantId ?? '').trim());
  };

  const getUserSettingsScopeKey = (resolvedTenantId: string, organisationScoped: boolean) =>
    organisationScoped ? `${ORGANISATION_SCOPE_KEY}:${resolvedTenantId}` : resolvedTenantId;
  const getConfigurations = async (keys: string[]) => {
    const tenantId = itemStore.activeTenant as string;

    try {
      // Initialize the tenant's configuration if not already present
      if (!storedConfigurations.value[tenantId]) {
        storedConfigurations.value[tenantId] = {};
      }

      // Determine which keys need to be fetched (those not cached)
      const keysToFetch = keys.filter(key => !(key in storedConfigurations.value[tenantId]));

      // Fetch only the missing keys
      if (keysToFetch.length > 0) {
        try {
          const response: TenantConfiguration[] = await nuxtApp.$apiService.Configuration.getConfigurations(keysToFetch);

          // Store only the value of each fetched configuration under the active tenant ID
          response.forEach((config) => {
            if (config.key) {
              storedConfigurations.value[tenantId][config.key] = config.value ?? null;
            }
          });
        } catch (apiError) {
          console.error('Error fetching tenant configurations:', apiError);
        }
      }

      // Return all requested keys (cached + fetched)
      return keys.map(key => storedConfigurations.value[tenantId][key] ?? null);

    } catch (error) {
      console.error('Unexpected error in getConfigurations:', error);
      // Return null for each key to indicate an error occurred
      return keys.map(() => null);
    }
  };

  const getConfigurationValueByKey: {
    <T extends ConfigurationValue>(key: string): Promise<T | null>;
    (key: string): Promise<ConfigurationValue | null>;
  } = async <T extends ConfigurationValue>(key: string): Promise<ConfigurationValue | null> => {
    const tenantId = itemStore.activeTenant as string; // Replace with your tenant retrieval logic

    try {
      // Check if the configuration for the key is already cached
      if (
        storedConfigurations.value[tenantId] &&
        storedConfigurations.value[tenantId][key] !== undefined
      ) {
        return coerceValue<T>(storedConfigurations.value[tenantId][key]);
      }

      // If not cached, fetch the configuration
      const configurations = await getConfigurations([key]); // Replace with your fetching logic

      // Cache the retrieved configurations
      if (!storedConfigurations.value[tenantId]) {
        storedConfigurations.value[tenantId] = {};
      }
      storedConfigurations.value[tenantId][key] = configurations[0];

      // Return the value for the requested key
      return coerceValue<T>(configurations[0]);
    } catch (error) {
      console.error('Error fetching configuration value by key:', error);
      return null;
    }
  };

  const getUserSettings = async (keys: string[], tenantId: string | null = itemStore.activeTenant as string | null, organisationScoped: boolean = false) => {
    const requestGeneration = userSettingsGeneration;
    const resolvedTenantId = getConfiguredTenantId(tenantId);

    if (!organisationScoped && !resolvedTenantId) return [];

    const scopeKey = getUserSettingsScopeKey(resolvedTenantId, organisationScoped);

    try {
      if (!storedUserSettings.value[scopeKey]) {
        storedUserSettings.value[scopeKey] = {};
      }

      const keysToFetch = keys.filter(key => !(key in storedUserSettings.value[scopeKey]));

      if (keysToFetch.length > 0) {
        const response: UserSetting[] = await nuxtApp.$apiService.Configuration.getUserSettings(keysToFetch, resolvedTenantId, organisationScoped);
        if (requestGeneration !== userSettingsGeneration) return keys.map(() => null);

        if (response.length > 0) {
          response.forEach((userSetting) => {
            if (userSetting.keyName) {
              storedUserSettings.value[scopeKey][userSetting.keyName] = userSetting;
            }
          });
        }
      }

      return keys.map(key => storedUserSettings.value[scopeKey][key] ?? null);
    } catch (error) {
      console.error('Error fetching user settings:', error);
      throw error;
    }
  };

  const getUserOrganizationSettings = async (key: string, tenantId: string | null = itemStore.activeTenant as string | null, organisationScoped: boolean = false): Promise<UserSetting | null> => {
    const requestGeneration = userSettingsGeneration;
    const resolvedTenantId = getConfiguredTenantId(tenantId);

    if (!organisationScoped && !resolvedTenantId) return null;

    const scopeKey = getUserSettingsScopeKey(resolvedTenantId, organisationScoped);
    const cached = storedUserSettings.value[scopeKey]?.[key];
    if (cached !== undefined) return cached;

    const [setting = null] = await nuxtApp.$apiService.Configuration.getUserSettings([key], resolvedTenantId, organisationScoped);
    if (requestGeneration !== userSettingsGeneration) return null;

    if (setting?.keyName) {
      if (!storedUserSettings.value[scopeKey]) {
        storedUserSettings.value[scopeKey] = {};
      }
      storedUserSettings.value[scopeKey][setting.keyName] = setting;
    }

    return setting;
  };

  const saveUserSetting = async (
    keyName: string,
    keyValue: string | null,
    tenantId: string | null = itemStore.activeTenant as string | null,
    organisationScoped: boolean = false
  ) => {
    const requestGeneration = userSettingsGeneration;
    const resolvedTenantId = getConfiguredTenantId(tenantId);

    if (!organisationScoped && !resolvedTenantId) return null;

    const scopeKey = getUserSettingsScopeKey(resolvedTenantId, organisationScoped);

    if (!storedUserSettings.value[scopeKey]) {
      storedUserSettings.value[scopeKey] = {};
    }

    const previousSetting = storedUserSettings.value[scopeKey][keyName];

    const userSetting: OrganisationScopedUserSetting = {
      keyName,
      keyValue,
      organisationScoped
    };

    storedUserSettings.value[scopeKey][keyName] = userSetting;

    try {
      const saved = await nuxtApp.$apiService.Configuration.saveUserSetting(userSetting, resolvedTenantId);
      return requestGeneration === userSettingsGeneration ? saved : null;
    } catch (error) {
      console.error('Error saving user setting:', error);

      if (requestGeneration === userSettingsGeneration) {
        if (previousSetting === undefined) {
          delete storedUserSettings.value[scopeKey][keyName];
        } else {
          storedUserSettings.value[scopeKey][keyName] = previousSetting;
        }
      }

      return null;
    }
  };

  // Helper methods
  const coerceValue = <T extends ConfigurationValue>(value: unknown): T | null => {
    // Todo: at some point, we should simply store the value in the store converted to the correct type. We could do this by using TenantConfiguration.type.
    if (value === null || value === undefined) {
      return null;
    }

    if (typeof value === "string") {
      // Handle boolean conversion
      if ((value === "true" || value === "false")) {
        return (value === "true") as T;
      }

      // Handle number conversion
      if (!isNaN(Number(value)) && z.number().safeParse(Number(value)).success) {
        return Number(value) as T;
      }

      // Return as string
      if (z.string().safeParse(value).success) {
        return value as T;
      }
    }

    return null; // If no conversion is possible, return null
  };

  // Cleanup
  const cleanConfigurationStore = () => {
    userSettingsGeneration++;
    storedConfigurations.value = {};
    storedUserSettings.value = {};
    useCookie('configurations').value = null;
  };

  return {
    storedConfigurations,
    storedUserSettings,
    getConfigurations,
    getConfigurationValueByKey,
    getUserSettings,
    getUserOrganizationSettings,
    saveUserSetting,
    cleanConfigurationStore,
  };
}, {
  persist: {
    key: 'configurations',
    storage: piniaPluginPersistedstate.cookies({sameSite: 'strict'}),
    pick: ['storedConfigurations']
  },
});